import { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { mdiDeleteOutline } from '@mdi/js'

import { makePublicUrl } from 'app/utils/publicUrl'
import { MdiIcon } from 'components/Icons'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { useAppDispatch, useAppSelector } from 'app/hooks'

import {
  Container,
  PaymentSystem,
  DeleteBtn,
  CardNumber,
  DateRow,
  OuterContainer,
  Last4Digits,
  CardDateLabel,
  MakeCardDefault,
} from './styles'

import { ModalText } from 'components/Modal/styles'
import { isExpiredCard } from 'app/utils/checkExpiration'
import { activeSubscriptionSelector } from 'features/home/selectors'
import { subscriptionManagementMessages as messages } from 'features/home/components/HomeSubscription/SubscriptionManagement/messages'
import type { PaymentMethodType } from 'features/paymentMethods/types'
import {
  detachPaymentMethodThunk,
  setDefaultPaymentMethodThunk,
} from 'features/paymentMethods/slice'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'

interface CardProps {
  isSelected?: boolean
  isSingleMethod?: boolean
  paymentMethod: PaymentMethodType
  onSelect: (id: string) => void
}

function Card({
  paymentMethod: { id, card, isDefault },
  onSelect,
  isSelected = false,
  isSingleMethod = false,
}: CardProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [detachAction, setDetachAction] = useState(false)

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const activeSubscription = useSelector(activeSubscriptionSelector.data)

  const detachPaymentMethod = useCallback(() => {
    if (rootDomainId) {
      dispatch(detachPaymentMethodThunk(id, rootDomainId))
    }
  }, [dispatch, id, rootDomainId])

  const setDefaultPaymentMethod = useCallback(() => {
    if (rootDomainId) {
      dispatch(setDefaultPaymentMethodThunk(id, rootDomainId))
    }
  }, [dispatch, id, rootDomainId])

  const openModal = (detachAction: boolean) => {
    setDetachAction(detachAction)
    setIsModalOpen(true)
  }
  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [setIsModalOpen])

  const performAction = useCallback(() => {
    detachAction ? detachPaymentMethod() : setDefaultPaymentMethod()
    closeModal()
  }, [detachPaymentMethod, closeModal, setDefaultPaymentMethod, detachAction])

  const formatDetachCardModalText = useCallback(() => {
    if (!isDefault) {
      return formatMessage(messages.removeText)
    }

    if (!isSingleMethod) {
      if (activeSubscription !== undefined) {
        return activeSubscription
          ? formatMessage(messages.removeDefaultCardWithSubscriptionText)
          : formatMessage(messages.removeDefaultCardWithoutSubscriptionText)
      }
      return formatMessage(messages.removeDefaultCardText)
    }

    return activeSubscription !== undefined
      ? formatMessage(messages.removeDefaultCardWithSubscriptionText)
      : formatMessage(messages.removeDefaultCardWithoutSubscriptionText)
  }, [activeSubscription, isDefault, isSingleMethod, formatMessage])

  const canCardDelete = useCallback(() => {
    if (!isDefault) {
      return false
    }

    if (!isSingleMethod) {
      if (activeSubscription !== undefined) {
        return activeSubscription ? true : false
      }
      return true
    }

    return activeSubscription !== undefined ? true : false
  }, [activeSubscription, isDefault, isSingleMethod])

  const isExpired = isExpiredCard(card)

  return (
    <OuterContainer onClick={() => onSelect(id)}>
      <Container isSelected={isSelected} isExpired={isExpired}>
        <CardNumber>
          <PaymentSystem
            src={makePublicUrl(`images/paymentLogos/${card.brand}.svg`)}
            alt={card.brand}
          />
          <Last4Digits
            isSelected={isSelected}
          >{`**** ${card.last4}`}</Last4Digits>
          {isDefault || isSingleMethod ? (
            <MakeCardDefault isDefault isSelected={isSelected}>
              <FormattedMessage {...messages.default} />
            </MakeCardDefault>
          ) : (
            !isExpired && (
              <MakeCardDefault onClick={() => openModal(false)}>
                <FormattedMessage {...messages.setAsDefault} />
              </MakeCardDefault>
            )
          )}
        </CardNumber>

        <DateRow>
          {isExpired ? (
            <CardDateLabel isExpired={isExpired}>
              <FormattedMessage {...messages.expired} />
            </CardDateLabel>
          ) : (
            <CardDateLabel isExpired={isExpired}>{`${
              card.exp_month > 9 ? card.exp_month : `0${card.exp_month}`
            }/${card.exp_year}`}</CardDateLabel>
          )}
          <DeleteBtn onClick={() => openModal(true)}>
            <MdiIcon path={mdiDeleteOutline} />
          </DeleteBtn>
        </DateRow>

        <ConfirmModal
          isOpen={isModalOpen}
          onDiscard={closeModal}
          isConfirmBtnDisabled={canCardDelete()}
          title={
            detachAction
              ? formatMessage(messages.removePaymentMethodModal, {
                  cardNumber: `****${card.last4}`,
                })
              : formatMessage(messages.setAsDefaultModal)
          }
          discardBtnTitle={<FormattedMessage {...messages.cancel} />}
          confirmBtnTitle={
            detachAction
              ? formatMessage(messages.yesRemoveTransfer)
              : formatMessage(messages.yesChange)
          }
          onConfirm={performAction}
          variant="primary"
        >
          <ModalText>
            {detachAction
              ? formatDetachCardModalText()
              : formatMessage(messages.defaultText)}
          </ModalText>
        </ConfirmModal>
      </Container>
    </OuterContainer>
  )
}

export default Card
